import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useClinicContext } from './useClinicContext';
import { useClinicPortalServiceType } from './clinicPortalServiceType';
import {
  fetchLongTermFrequentOne,
  createLongTermFrequent,
  updateLongTermFrequent,
  deleteLongTermFrequent,
  fetchMonthData,
  fetchMasterData,
  type LongTermFrequentPayload,
} from '@/services/longTermFrequentService';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage, DEFAULT_FETCH_ERROR_MESSAGE } from '@/utils/apiError';
import {
  UPDATE_SUCCESS_MESSAGE,
  UPDATE_ERROR_MESSAGE,
  DELETE_SUCCESS_MESSAGE,
} from '@/constants/messages';

export function useLongTermFrequentOne(patientId: string | undefined, id: string | undefined) {
  const { tenant, clinicNumber } = useClinicContext();
  // The record is read per service (LongTermFrequentController@show reads X-Service-Type and even
  // picks a different Resource class), so the service belongs in the cache key. (HIT-11387)
  const serviceType = useClinicPortalServiceType();
  const toast = useAppToast();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['long-term-frequents', 'one', tenant, clinicNumber, patientId, id, serviceType],
    queryFn: () => fetchLongTermFrequentOne(tenant, clinicNumber, patientId!, id!),
    enabled: !!tenant && !!clinicNumber && !!patientId && !!id && id !== 'new',
    staleTime: 0,
    refetchOnMount: 'always',
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  return { data, isLoading, isError, refetch };
}

/** Create long term frequent mutation */
export function useLongTermFrequentCreate(patientId: string) {
  const { tenant, clinicNumber } = useClinicContext();
  const queryClient = useQueryClient();
  const toast = useAppToast();

  return useMutation({
    mutationFn: (payload: LongTermFrequentPayload) =>
      createLongTermFrequent(tenant, clinicNumber, patientId, payload),
    onSuccess: response => {
      queryClient.removeQueries({ queryKey: ['long-term-frequents', 'list'] });
      if (response.success) {
        toast.success(response.message ?? UPDATE_SUCCESS_MESSAGE);
      }
    },
    onError: (error: any) => {
      const status = error?.response?.status;
      if (status === 422 && error?.response?.data?.errors) {
        toast.error(UPDATE_ERROR_MESSAGE);
      } else {
        toast.error(getApiErrorMessage(error, UPDATE_ERROR_MESSAGE));
      }
    },
  });
}

/** Update long term frequent mutation */
export function useLongTermFrequentUpdate(patientId: string) {
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: LongTermFrequentPayload }) =>
      updateLongTermFrequent(tenant, clinicNumber, patientId, id, payload),
    onSuccess: response => {
      if (response.success) {
        toast.success(response.message ?? UPDATE_SUCCESS_MESSAGE);
      }
    },
    onError: error => {
      toast.error(getApiErrorMessage(error, UPDATE_ERROR_MESSAGE));
    },
  });
}

/** Delete long term frequent mutation (list page) */
export function useLongTermFrequentDelete(patientId?: string) {
  const queryClient = useQueryClient();
  const { tenant, clinicNumber } = useClinicContext();

  return useMutation({
    mutationFn: async (id: number) => {
      try {
        return await deleteLongTermFrequent(tenant, clinicNumber, patientId || '', id);
      } catch (error: any) {
        if (error?.response?.status === 404) {
          return { success: true, message: DELETE_SUCCESS_MESSAGE };
        }
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['long-term-frequents', 'list'],
        refetchType: 'active',
      });
    },
  });
}

/**
 * Fetch ALL month data in a single call (consent + visits + massager + prev month).
 * Equivalent to RA2's prev() AJAX — 1 request per month selection.
 */
export function useLongTermFrequentMonthData(
  patientId: string | undefined,
  visitYearmonth: string | undefined
) {
  const { tenant, clinicNumber } = useClinicContext();
  // Scoped by X-Service-Type (LongTermFrequentController@monthData). (HIT-11387)
  const serviceType = useClinicPortalServiceType();
  const toast = useAppToast();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: [
      'long-term-frequents',
      'month-data',
      tenant,
      clinicNumber,
      patientId,
      visitYearmonth,
      serviceType,
    ],
    queryFn: () => fetchMonthData(tenant, clinicNumber, patientId!, visitYearmonth!),
    enabled: !!tenant && !!clinicNumber && !!patientId && !!visitYearmonth && /^\d{6}$/.test(visitYearmonth),
    staleTime: 30_000,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  return { data: data?.data, isLoading, isError, refetch };
}

/** Fetch master data (recovery_types, valuation_types) */
export function useLongTermFrequentMasterData(patientId: string | undefined) {
  const { tenant, clinicNumber } = useClinicContext();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['long-term-frequents', 'master-data', tenant, clinicNumber],
    queryFn: () => fetchMasterData(tenant, clinicNumber, patientId!),
    enabled: !!tenant && !!clinicNumber && !!patientId,
    staleTime: 5 * 60 * 1000, // 5 minutes - master data rarely changes
  });

  return {
    recoveryTypes: data?.data?.recovery_types ?? [],
    valuationTypes: data?.data?.valuation_types ?? [],
    isLoading,
    isError,
  };
}
